import React, { useState, useEffect, useRef, Fragment } from "react";
import { Link, useNavigate } from "react-router";
import Select from "react-select";
import { toast } from "react-toastify";
import { Menu, Transition } from "@headlessui/react";
import { DotsVerticalIcon } from "@heroicons/react/solid";
import Pagination from "../../shared/common/pagination";
import Loader from "../../shared/loader/loader";
import SelectPageSize from "../../shared/common/pageSizeDropdown";
import {
  getCannedTestTypes,
  getCannedTests,
  deleteCannedTest,
  getSubjects,
} from "../../../api/cannedTests.service";
import {
  EditIcon,
  FilterIcon,
  ResetIcon,
  CaretUpSolidIcon,
  CaretDownSolidIcon,
  AddIcon,
  DeleteIcon,
  CSVIcon,
  PreviewIcon,
  ApproveIcon,
  Disapprove,
} from "../../../assets/icons/index";
import constant from "../../../utils/constant";
import SelectList from "../../../model/shared/selectList";
import DeleteModal from "../../shared/common/deleteModal";
import ExportToCSV from "../../shared/common/exportToCsv";
import PreviewModal from "../questions/previewModal";
import ActiveRowLine from "../../shared/common/activeRowLine";
import AllowedTo from "../../shared/common/rbac";
import Permissions from "../../../utils/permissions";
import AccessDenied from "../../shared/common/accessDenied";
import MultiStateButton from "../../shared/common/multiStateButton";
import DropDownComponent from "../questions/metadataDropDownList";
import { getContentArea } from "../../../api/contentArea.service";
import { PagingDetailsLabel } from "../../shared/common/PagingDetailsLabel";

const activeMultiState = [
  { label: "Active", value: true },
  { label: "Both", value: null, selected: true },
  { label: "Inactive", value: false },
];
const CannedTests: React.FC = () => {
  const initialState = {
    cannedTestId: 0,
    grade: 0,
    state: 0,
    cannedTestType: 0,
    isActive: undefined,
    pageNumber: 1,
    pageSize: 10,
    orderByColumn: "cannedTestId",
    sortByDesc: false,
    isApproved: true,
    requiresSubscription: false,
    name: undefined,
    subjectId: undefined,
    contentAreaId: undefined,
  };
  const [resetFlag, setResetFlag] = useState<boolean>(false);
  const [grade, setGrade] = useState<Array<SelectList>>([]);
  const [state, setState] = useState<Array<SelectList>>([]);
  const [contentAreas, setContentAreas] = useState<Array<SelectList>>([]);
  const [subjects, setSubjects] = useState<Array<SelectList>>([]);
  const [cannedTestType, setCannedTestType] = useState<Array<SelectList>>([]);
  const [cannedTests, setCannedTests] = useState<any>([]);
  const [filterInput, setFilterInput] = useState<any>(initialState);
  const [loading, setLoading] = useState<boolean>(false);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [cannedTestDelete, setCannedTestDelete] = useState<number>(0);
  const [showDeletePopup, setShowDeletePopup] = useState<boolean>(false);
  const paginationRef: any = useRef(undefined);
  const pageSizeRef: any = useRef(undefined);
  const childRef: any = useRef(undefined);
  const [previewId, setPreviewId] = useState<string>("");
  const [spanishPreviewId, setSpanishPreviewId] = useState<string>("");
  const [englishPreviewId, setEnglishPreviewId] = useState<string>("");
  const [open, setOpen] = useState<boolean>(false);
  const navigate = useNavigate();

  const MAX_VISIBLE_STATES = 5;

  useEffect(() => {
    setLoading(true);
    getStateData();
    getGradeData();
    getCannedTestTypeData();
    loadContentAreas();
    loadSubjects();
    setLoading(false);
  }, []);

  /** Code to implement input type number to support all browsers */
  useEffect(() => {
    const input = document.getElementById("cannedTestId");
    if (input) {
      input.addEventListener("keypress", (e: any) => {
        const keyValue = e.key;
        if (!e.target.value && e.key == 0) {
          e.preventDefault();
        } else {
          if (!/[0-9.]/.test(keyValue)) {
            e.preventDefault();
          }
        }
      });
    }
  }, []);

  const getStateData = async () => {
    const service = await import("../../../api/state.service");
    const response = service.getStateAll();
    const data = (await response).data;
    let newStateData = [];
    if (data.data.length > 0) {
      newStateData = data.data.map((eachStateData) => {
        return {
          value: eachStateData.stateId || 0,
          label: `${eachStateData.name}`,
        };
      });
    }
    setState(newStateData);
    setLoading(false);
  };

  const getGradeData = async () => {
    const service = await import("../../../api/grade.service");
    const response = service.getAllgrade(100, 1);
    const data = (await response).data;
    let newGradeData = [];
    if (data.data.length > 0) {
      data.data.sort(
        (firstItem, secondItem) => firstItem.gradeId - secondItem.gradeId
      );
      newGradeData = data.data.map((eachGradeData) => {
        return {
          value: eachGradeData.gradeId || 0,
          label: `${eachGradeData.name}`,
        };
      });
    }
    setGrade(newGradeData);
  };

  const getCannedTestTypeData = async () => {
    const response = getCannedTestTypes();
    const data = (await response).data;
    let newTestTypeData = [];
    if (data.length > 0) {
      newTestTypeData = data.map((eachTestTypeData) => {
        return {
          value: eachTestTypeData.id || 0,
          label: `${eachTestTypeData.name}`,
        };
      });
    }
    setCannedTestType(newTestTypeData);
  };

  const loadContentAreas = async () => {
    const response = await getContentArea();
    const items = response.data.data.map(
      (item) => new SelectList(item.contentAreaId, item.name)
    );
    setContentAreas(items);
  };
  const loadSubjects = async () => {
    const response = await getSubjects();
    const items = response.data.map(
      (item) => new SelectList(item.subjectId, item.name)
    );
    setSubjects(items);
  };

  const updateDropdownValues = (updateObj) => {
    if (updateObj.type == "State") {
      setFilterInput({
        ...filterInput,
        stateId: updateObj.value || 0,
        stateName: updateObj.label || 0,
      });
    } else if (updateObj.type == "Grade") {
      setFilterInput({
        ...filterInput,
        gradeId: updateObj.value || 0,
        gradeName: updateObj.label || 0,
      });
    } else if (updateObj.type == "CannedTestType") {
      setFilterInput({
        ...filterInput,
        cannedTestTypeId: updateObj.value || 0,
        cannedTestTypeName: updateObj.label || 0,
      });
    } else if (updateObj.type == "IsApproved") {
      setFilterInput({
        ...filterInput,
        isApproved: updateObj.value || false,
      });
    } else if (updateObj.type == "RequiresSubscription") {
      setFilterInput({
        ...filterInput,
        requiresSubscription: updateObj.value,
      });
    }
  };

  const handleChangePageSize = async (pageSize: number) => {
    if (pageSize === filterInput.pageSize) return;

    const filter = {
      ...filterInput,
      pageSize: pageSize,
    };
    setFilterInput(filter);
    await bindCannedTestData(filter);
  };

  const handleChangePageNumber = async (pageNumber: number) => {
    if (pageNumber === filterInput.pageNumber) return;

    const filter = {
      ...filterInput,
      pageNumber: pageNumber,
    };
    setFilterInput(filter);
    await bindCannedTestData(filter);
  };

  const handleChangeSort = async (column: string) => {
    let filter = filterInput;
    if (filterInput.orderByColumn === column) {
      filter = {
        ...filterInput,
        sortByDesc: !filterInput.sortByDesc,
      };
    } else {
      filter = {
        ...filterInput,
        sortByDesc: false,
        orderByColumn: column,
      };
    }
    setFilterInput(filter);
    await bindCannedTestData(filter);
  };

  const handleContentAreaChange = (selectedItem: any) => {
    setFilterInput({
      ...filterInput,
      contentAreaId: selectedItem,
    });
  };

  const handleSubjectChange = (selectedItem: any) => {
    setFilterInput({
      ...filterInput,
      subjectId: selectedItem,
    });
  };

  const handleFilterClick = async (e) => {
    e.preventDefault();
    await bindCannedTestData(filterInput);
  };

  const handleResetClick = () => {
    setFilterInput(initialState);
    setResetFlag(!resetFlag);
    setCannedTests([]);
  };

  function redirectToCannedTest(cannedTestId: number) {
    if (cannedTestId > 0) {
      navigate(
        constant.CANNEDTESTDETAIL.replace(
          ":cannedtestId",
          cannedTestId.toString()
        )
      );
    } else {
      navigate(constant.CANNEDTEST);
    }
  }

  const openModal = (id, spanishId) => {
    setOpen(true);
    setPreviewId(id);
    setSpanishPreviewId(spanishId);
    setEnglishPreviewId(id);
  };

  const popUpClose = (value) => {
    setOpen(value);
    setPreviewId("");
    setSpanishPreviewId("");
    setEnglishPreviewId("");
  };

  const bindCannedTestData = async (filter: any) => {
    console.log("Filter", filterInput);
    setLoading(true);
    let response: any = getCannedTests(
      filter.stateId,
      filter.gradeId,
      filter.cannedTestTypeId,
      filter.isActive,
      filter.isApproved,
      filter.pageNumber,
      filter.pageSize,
      filter.orderByColumn,
      filter.sortByDesc ? "desc" : "asc",
      filter.cannedTestId,
      filter.subjectId,
      filter.contentAreaId,
      filter.name,
      filter.requiresSubscription || undefined
    );
    response = (await response).data;
    setLoading(false);
    if (response && response.data && response.data.length) {
      setCannedTests(response.data);
      setTotalPages(Math.ceil(response.totalRecords / filterInput.pageSize));
      setTotalRecords(response.totalRecords);
    } else {
      setCannedTests([]);
      setTotalPages(0);
    }
  };
  function confirmDelete(idToDelete: number) {
    if (idToDelete) {
      setShowDeletePopup(true);
      setCannedTestDelete(idToDelete);
    }
  }

  function deleteRecord() {
    const cannedTest = cannedTests.filter(
      (t) => t.cannedTestId == cannedTestDelete
    )[0];
    deleteCannedTest(cannedTestDelete).then(async () => {
      setShowDeletePopup(false);
      setCannedTestDelete(0);
      await bindCannedTestData(filterInput);
    });
  }

  function closeDeletePopup() {
    setShowDeletePopup(false);
    setCannedTestDelete(0);
  }

  const exportFn = async () => {
    setLoading(true);
    const response: any = getCannedTests(
      filterInput.stateId,
      filterInput.gradeId,
      filterInput.cannedTestTypeId,
      filterInput.isActive,
      filterInput.isApproved,
      1,
      9999,
      filterInput.orderByColumn,
      filterInput.sortByDesc ? "desc" : "asc",
      filterInput.cannedTestId,
      filterInput.subjectId,
      filterInput.contentAreaId,
      filterInput.name,
      filterInput.requiresSubscription || undefined
    );

    const data = (await response).data;
    setLoading(false);

    if (data && data.data && data.data.length > 0) {
      childRef?.current?.onDownload(data.data, "cannedTest.csv");
    } else {
      toast.warning("No records to download");
    }
  };
  const handleSubjectRedirect = (subjectId: number, subjectType: number) => {
    if (subjectType === constant.SubjectType.LO) {
      navigate(
        constant.LIFTOFFSUBJECTEDIT.replace(":id", subjectId.toString()),
        { state: { subjectType } }
      );
    } else if (subjectId) {
      navigate(constant.SUBJECTDETAIL.replace(":id", subjectId.toString()), {
        state: { subjectType },
      });
    } else {
      navigate(constant.ADDSUBJECT);
    }
  };

  const gradeRedirect = (gradeId: string) => {
    navigate(
      constant.EDITGRADE.replace(":id", gradeId ? gradeId.toString() : "")
    );
  };

  function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
  }

  const buildCannedTestName = (cannedTest: any) => {
    if (cannedTest.name != null && cannedTest.name.length > 0)
      return cannedTest.name;
    if (cannedTest.cannedTestTypeId === 1) return "Pre-Test";
    if (cannedTest.cannedTestTypeId === 2) return "Post-Test";
  };

  return (
    <AllowedTo
      perform={Permissions.canned_test_view}
      no={() => <AccessDenied />}
    >
      {loading && <Loader />}
      <PreviewModal
        modal={openModal}
        show={open}
        changeValue={popUpClose}
        activityRef={previewId}
        activityIdPreview={previewId}
        spanishActivityRef={spanishPreviewId}
        englishActivityRef={englishPreviewId}
        setPreviewId={setPreviewId}
      ></PreviewModal>

      {showDeletePopup && (
        <DeleteModal
          closeDeleteModal={closeDeletePopup}
          deleteRecord={deleteRecord}
        />
      )}

      <div className="flex-1 overflow-auto">
        <div className=" h-auto min-h-half w-full rounded-bl-3xl bg-site-teal px-8  py-8 pt-0 pb-14 mt-7 ">
          <div className="relative  py-1">
            <div className="overlay-image-main pointer-events-none fixed left-0 top-0 w-screen h-screen z-20"></div>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-light text-gray-900">Canned Tests</h3>
              <AllowedTo perform={Permissions.canned_test_create}>
                <button
                  type="button"
                  className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 rounded-md shadow-xs text-white dark:text-white bg-primary-color hover:bg-secondary-color focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color hover:shadow-lg transition ease-in-out duration-300"
                  onClick={() => {
                    redirectToCannedTest(0);
                  }}
                >
                  <AddIcon className="w-5"></AddIcon>
                  Add Canned Test
                </button>
              </AllowedTo>
            </div>
            <form action="#" onSubmit={handleFilterClick}>
              <div className="pt-6 border-b pb-6">
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-2 sm:col-span-2">
                    <div>
                      <div className="text-sm ">
                        <label className="text-sm text-gray-700">
                          Canned Test Id
                        </label>
                        <div className="contentArea">
                          <input
                            id="cannedTestId"
                            name="cannedTestId"
                            type="text"
                            autoComplete="off"
                            className="block mt-1 w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
                            value={filterInput.cannedTestId || ""}
                            onChange={(e) =>
                              setFilterInput({
                                ...filterInput,
                                cannedTestId: Number(e.target.value),
                              })
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-span-2 sm:col-span-2">
                    <div>
                      <div className="text-sm ">
                        <label className="font-medium text-gray-700 inline-block mb-1">
                          State
                        </label>
                        <Select
                          value={{
                            label: filterInput.stateName,
                            value: filterInput.stateId,
                          }}
                          onChange={(result) =>
                            updateDropdownValues({
                              ...result,
                              type: "State",
                            })
                          }
                          options={state}
                          theme={(theme) => ({
                            ...theme,
                            colors: {
                              ...theme.colors,
                              primary25: "#B3B3B3",
                              primary: "#041E42",
                            },
                          })}
                          styles={{
                            control: (base, state) => ({
                              ...base,
                              background:
                                document.getElementsByClassName("dark")
                                  ?.length > 0
                                  ? "#1A1C23"
                                  : "white",
                              borderColor:
                                document.getElementsByClassName("dark")
                                  ?.length > 0
                                  ? "#6B7280"
                                  : "#D1D5DB",
                              borderWidth: "1px",
                              "&:hover": {
                                borderColor:
                                  state.isFocused &&
                                  document.getElementsByClassName("dark")
                                    ?.length > 0
                                    ? "#6b7280"
                                    : state.isFocused
                                    ? "#4c5563"
                                    : "",
                              },
                            }),
                            singleValue: (base) => ({
                              ...base,
                              color:
                                document.getElementsByClassName("dark")
                                  ?.length > 0
                                  ? "white"
                                  : "#374051",
                            }),
                            input: (base) => ({
                              ...base,
                              color:
                                document.getElementsByClassName("dark")
                                  ?.length > 0
                                  ? "white"
                                  : "",
                            }),
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-span-2 sm:col-span-2">
                    <div>
                      <div className="text-sm">
                        <label className="font-medium text-gray-700 inline-block mb-1">
                          Grade
                        </label>
                        <Select
                          value={{
                            label: filterInput.gradeName,
                            value: filterInput.gradeId,
                          }}
                          onChange={(result) =>
                            updateDropdownValues({
                              ...result,
                              type: "Grade",
                            })
                          }
                          options={grade}
                          theme={(theme) => ({
                            ...theme,
                            colors: {
                              ...theme.colors,
                              primary25: "#B3B3B3",
                              primary: "#041E42",
                            },
                          })}
                          styles={{
                            control: (base, state) => ({
                              ...base,
                              background:
                                document.getElementsByClassName("dark")
                                  ?.length > 0
                                  ? "#1A1C23"
                                  : "white",
                              borderColor:
                                document.getElementsByClassName("dark")
                                  ?.length > 0
                                  ? "#6B7280"
                                  : "#D1D5DB",
                              borderWidth: "1px",
                              "&:hover": {
                                borderColor:
                                  state.isFocused &&
                                  document.getElementsByClassName("dark")
                                    ?.length > 0
                                    ? "#6b7280"
                                    : state.isFocused
                                    ? "#4c5563"
                                    : "",
                              },
                            }),
                            singleValue: (base) => ({
                              ...base,
                              color:
                                document.getElementsByClassName("dark")
                                  ?.length > 0
                                  ? "white"
                                  : "#374051",
                            }),
                            input: (base) => ({
                              ...base,
                              color:
                                document.getElementsByClassName("dark")
                                  ?.length > 0
                                  ? "white"
                                  : "",
                            }),
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-span-2 sm:col-span-2">
                    <div>
                      <div className="text-sm">
                        <label className="font-medium text-gray-700 inline-block mb-1">
                          Canned Test Type
                        </label>
                        <Select
                          value={{
                            label: filterInput.cannedTestTypeName,
                            value: filterInput.cannedTestTypeId,
                          }}
                          onChange={(result) =>
                            updateDropdownValues({
                              ...result,
                              type: "CannedTestType",
                            })
                          }
                          options={cannedTestType}
                          theme={(theme) => ({
                            ...theme,
                            colors: {
                              ...theme.colors,
                              primary25: "#B3B3B3",
                              primary: "#041E42",
                            },
                          })}
                          styles={{
                            control: (base, state) => ({
                              ...base,
                              background:
                                document.getElementsByClassName("dark")
                                  ?.length > 0
                                  ? "#1A1C23"
                                  : "white",
                              borderColor:
                                document.getElementsByClassName("dark")
                                  ?.length > 0
                                  ? "#6B7280"
                                  : "#D1D5DB",
                              borderWidth: "1px",
                              "&:hover": {
                                borderColor:
                                  state.isFocused &&
                                  document.getElementsByClassName("dark")
                                    ?.length > 0
                                    ? "#6b7280"
                                    : state.isFocused
                                    ? "#4c5563"
                                    : "",
                              },
                            }),
                            singleValue: (base) => ({
                              ...base,
                              color:
                                document.getElementsByClassName("dark")
                                  ?.length > 0
                                  ? "white"
                                  : "#374051",
                            }),
                            input: (base) => ({
                              ...base,
                              color:
                                document.getElementsByClassName("dark")
                                  ?.length > 0
                                  ? "white"
                                  : "",
                            }),
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-span-2 flex items-center mt-6">
                    <MultiStateButton
                      labels={activeMultiState}
                      onSelect={(e) =>
                        setFilterInput({ ...filterInput, isActive: e.value })
                      }
                      onReset={resetFlag}
                    />
                  </div>
                  <div className="col-span-1 flex items-end">
                    <div className="flex flex-col mb-2 gap-2">
                      <div className="flex items-center">
                        <input
                          id="approved"
                          name="approved"
                          type="checkbox"
                          checked={filterInput.isApproved}
                          className=" focus:ring-primary-color h-4 w-4 text-primary-color border-gray-300 rounded"
                          onChange={(e) =>
                            updateDropdownValues({
                              value: !filterInput.isApproved,
                              type: "IsApproved",
                            })
                          }
                        />
                        <label className="ml-2 text-sm text-gray-700">
                          Approved
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          id="requiresSubscription"
                          name="requiresSubscription"
                          type="checkbox"
                          checked={filterInput.requiresSubscription}
                          className="focus:ring-primary-color h-4 w-4 text-primary-color border-gray-300 rounded"
                          onChange={() =>
                            updateDropdownValues({
                              value: !filterInput.requiresSubscription,
                              type: "RequiresSubscription",
                            })
                          }
                        />
                        <label className="ml-2 text-sm text-gray-700 whitespace-nowrap">
                          Subscription Required
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-12 gap-6 mt-4">
                  <div className="col-span-2 sm:col-span-2 mt-1 text-sm">
                    <label className="text-sm text-gray-700">Subject</label>
                    <DropDownComponent
                      dropName={"subjectList"}
                      dropValue={subjects}
                      resetDropdown={resetFlag}
                      defaultValue={""}
                      dropSelect={handleSubjectChange}
                    ></DropDownComponent>
                  </div>
                  <div className="col-span-2 sm:col-span-2 mt-1 text-sm">
                    <label className="text-sm text-gray-700">Name</label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      autoComplete="off"
                      className="block mt-1 w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
                      value={filterInput.name || ""}
                      onChange={(e) =>
                        setFilterInput({ ...filterInput, name: e.target.value })
                      }
                    />
                  </div>
                  <div className="col-span-2 sm:col-span-2 mt-1 text-sm">
                    <label className="text-sm text-gray-700">
                      Content Area
                    </label>
                    <DropDownComponent
                      dropName={"contentAreaList"}
                      dropValue={contentAreas}
                      resetDropdown={resetFlag}
                      defaultValue={""}
                      dropSelect={handleContentAreaChange}
                    ></DropDownComponent>
                  </div>
                </div>
                <div className="flex justify-start">
                  <button
                    type="submit"
                    className="mt-4 inline-flex items-center text-sm text-white dark:text-white gap-2 bg-primary-color hover:bg-secondary-color py-1.5 px-4 rounded-lg"
                  >
                    <FilterIcon className="w-3 h-3"></FilterIcon>
                    <span>Filter</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleResetClick}
                    className="mt-4 py-1.5 px-4 inline-flex ml-3 items-center text-sm rounded-lg text-gray-700 text-primary-color border border-gray-300 gap-2 bg-white hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color "
                  >
                    <ResetIcon className="w-3 h-3"></ResetIcon>
                    <span>Reset Filter</span>
                  </button>
                </div>
              </div>
              <div className="flex justify-end mb-2">
                <button
                  type="button"
                  onClick={() => exportFn()}
                  className="mt-4 py-1.5 px-4 inline-flex ml-auto items-center text-sm rounded-lg text-gray-700 text-primary-color border border-gray-300 gap-2 bg-white hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color "
                >
                  <CSVIcon className="w-3 h-3 dark:text-white stroke-current"></CSVIcon>
                  <span>Export</span>
                </button>
                <ExportToCSV ref={childRef} />
              </div>
            </form>

            <div id="dataContainer" className="mt-4">
              <div className="flex flex-col mt-1">
                <div className="-my-2 relative sm:-mx-6 lg:-mx-8">
                  <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div className="shadow border-b border-gray-200 sm:rounded-lg">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-primary-color">
                          <tr>
                            <th
                              scope="col"
                              className="px-5 py-2 whitespace-nowrap font-light text-sm text-left text-white dark:text-white tracking-wider rounded-tl-lg cursor-pointer"
                              onClick={() => handleChangeSort("cannedTestId")}
                            >
                              <div className="flex">
                                Id
                                {filterInput.orderByColumn ===
                                  "cannedTestId" && (
                                  <span className="">
                                    {!filterInput.sortByDesc ? (
                                      <CaretUpSolidIcon className="w-5 h-5"></CaretUpSolidIcon>
                                    ) : (
                                      <CaretDownSolidIcon className="w-5 h-5"></CaretDownSolidIcon>
                                    )}
                                  </span>
                                )}
                              </div>
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2  whitespace-nowrap font-light text-sm text-center text-white dark:text-white tracking-wider"
                            >
                              <div className="flex">Name</div>
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2  whitespace-nowrap font-light text-sm text-center text-white dark:text-white tracking-wider cursor-pointer"
                              onClick={() => handleChangeSort("subject")}
                            >
                              <span className="flex">
                                Subject
                                {filterInput.orderByColumn === "subject" && (
                                  <span className="subject">
                                    {!filterInput.sortByDesc ? (
                                      <CaretUpSolidIcon className="w-5 h-5"></CaretUpSolidIcon>
                                    ) : (
                                      <CaretDownSolidIcon className="w-5 h-5"></CaretDownSolidIcon>
                                    )}
                                  </span>
                                )}
                              </span>
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 whitespace-nowrap font-light text-sm text-left text-white dark:text-white tracking-wider"
                            >
                              <span className="flex">State</span>
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 whitespace-nowrap font-light text-sm text-left text-white dark:text-white tracking-wider"
                            >
                              <span className="flex">Grade</span>
                            </th>

                            <th
                              scope="col"
                              className="px-3 py-2 whitespace-nowrap  font-light text-sm text-center text-white dark:text-white tracking-wider cursor-pointer"
                              onClick={() => handleChangeSort("type")}
                            >
                              <span className="flex">
                                Type
                                {filterInput.orderByColumn === "type" && (
                                  <span className="cursor-pointer">
                                    {!filterInput.sortByDesc ? (
                                      <CaretUpSolidIcon className="w-5 h-5"></CaretUpSolidIcon>
                                    ) : (
                                      <CaretDownSolidIcon className="w-5 h-5"></CaretDownSolidIcon>
                                    )}
                                  </span>
                                )}
                              </span>
                            </th>
                            <th
                              scope="col"
                              className="w-10 px-3 py-2 font-light text-sm text-center text-white dark:text-white tracking-wider rounded-tr-lg"
                            >
                              Action
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {cannedTests.map((cannedTest, index) => (
                            <tr
                              key={cannedTest.cannedTestId}
                              className={
                                index % 2 === 0 ? "bg-white" : "bg-gray-100"
                              }
                            >
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-left font-normal text-gray-500 relative">
                                <ActiveRowLine isActive={cannedTest.isActive} />
                                <Link
                                  className="cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
                                  to={constant.CANNEDTESTDETAIL.replace(
                                    ":cannedtestId",
                                    cannedTest.cannedTestId.toString()
                                  )}
                                >
                                  {cannedTest.cannedTestId}
                                </Link>
                              </td>
                              <td className="px-3  py-2 capitalize text-sm text-left font-normal text-gray-500">
                                {buildCannedTestName(cannedTest)}
                              </td>
                              <td
                                className="px-3  py-2 cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
                                onClick={(e) => {
                                  handleSubjectRedirect(
                                    cannedTest.subjectId,
                                    cannedTest.subjectTypeId
                                  );
                                }}
                              >
                                <Link
                                  className="cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
                                  to={constant.SUBJECTDETAIL.replace(
                                    ":id",
                                    cannedTest.subjectId
                                  )}
                                >
                                  {cannedTest.subject}
                                </Link>
                              </td>
                              <td
                                className="px-3 py-2   text-sm text-left font-normal text-gray-500"
                                title={
                                  cannedTest.states.length > MAX_VISIBLE_STATES
                                    ? cannedTest.states
                                        .map((ds) => ds.name)
                                        .join("\r\n")
                                    : ""
                                }
                              >
                                {cannedTest.states
                                  .slice(0, MAX_VISIBLE_STATES)
                                  .map((ds) => ds.name)
                                  .join(", ")}
                                {cannedTest.states.length > MAX_VISIBLE_STATES
                                  ? `...${
                                      cannedTest.states.length -
                                      MAX_VISIBLE_STATES
                                    } more`
                                  : ""}
                              </td>
                              <td className="px-3 py-2 text-sm text-left font-normal text-gray-500">
                                {cannedTest.grades.length > 0 &&
                                  cannedTest.grades.map((ds, index) => (
                                    <>
                                      {ds.name}
                                      {cannedTest.grades.length > 1 &&
                                        index <
                                          cannedTest.grades.length - 1 && (
                                          <span>, </span>
                                        )}
                                    </>
                                  ))}
                              </td>
                              <td className="px-3  py-2 capitalize text-sm text-left font-normal text-gray-500">
                                {cannedTest.cannedTestType}
                              </td>
                              <td className="flex whitespace-wrap text-sm  px-3 py-2 text-center font-normal text-gray-700 relative justify-end">
                                <div className="flex space-x-2 justify-end items-center">
                                  {cannedTest.isApproved ? (
                                    <ApproveIcon title="Approved" />
                                  ) : (
                                    <Disapprove />
                                  )}
                                  <Menu
                                    as="div"
                                    className="relative flex justify-end items-center"
                                  >
                                    <Menu.Button className="h-7 bg-white inline-flex items-center justify-center text-gray-400 rounded-full hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bg-primary-color">
                                      <span className="sr-only">
                                        Open options
                                      </span>
                                      <DotsVerticalIcon
                                        className="w-5 h-5"
                                        aria-hidden="true"
                                      />
                                    </Menu.Button>

                                    <Transition
                                      as={Fragment}
                                      enter="transition ease-out duration-100"
                                      enterFrom="transform opacity-0 scale-95"
                                      enterTo="transform opacity-100 scale-100"
                                      leave="transition ease-in duration-75"
                                      leaveFrom="transform opacity-100 scale-100"
                                      leaveTo="transform opacity-0 scale-95"
                                    >
                                      <Menu.Items className="mx-3 origin-top-right absolute right-7 top-0 w-48 mt-1 rounded-md shadow-lg z-10 bg-white ring-1 ring-black ring-opacity-5 divide-y divide-gray-200 focus:outline-none">
                                        <div className="py-1">
                                          <Menu.Item>
                                            {({ active }) => (
                                              <a
                                                onClick={() =>
                                                  openModal(
                                                    cannedTest.englishExternalReferenceId,
                                                    cannedTest.spanishExternalReferenceId
                                                  )
                                                }
                                                className={
                                                  "text-gray-500 group flex items-center px-4 py-2 text-sm"
                                                }
                                              >
                                                <PreviewIcon className="text-gray-500  mr-3 h-5 w-5 text-xs font-light cursor-pointer hover:text-secondary-color transaction ease-in-out duration-300" />
                                                Preview
                                              </a>
                                            )}
                                          </Menu.Item>
                                          <AllowedTo
                                            perform={
                                              Permissions.canned_test_modify
                                            }
                                          >
                                            <Menu.Item>
                                              {({ active }) => (
                                                <a
                                                  onClick={(e) => {
                                                    redirectToCannedTest(
                                                      cannedTest.cannedTestId
                                                    );
                                                  }}
                                                  className={classNames(
                                                    active
                                                      ? "bg-gray-100 text-gray-900"
                                                      : "text-gray-700",
                                                    "group flex items-center px-4 py-2 text-sm"
                                                  )}
                                                >
                                                  <EditIcon className="mr-3 h-5 w-5 text-xs font-light cursor-pointer hover:text-secondary-color transaction ease-in-out duration-300" />
                                                  Edit
                                                </a>
                                              )}
                                            </Menu.Item>
                                          </AllowedTo>
                                        </div>
                                        <AllowedTo
                                          perform={
                                            Permissions.canned_test_delete
                                          }
                                        >
                                          <div className="py-1">
                                            <Menu.Item>
                                              {({ active }) => (
                                                <a
                                                  onClick={() =>
                                                    confirmDelete(
                                                      cannedTest.cannedTestId
                                                    )
                                                  }
                                                  className={classNames(
                                                    active
                                                      ? "bg-gray-100 text-gray-900"
                                                      : "text-gray-700",
                                                    "group flex items-center px-4 py-2 text-sm"
                                                  )}
                                                >
                                                  <DeleteIcon className="mr-3 h-5 w-5 text-xs font-light cursor-pointer hover:text-red-500 transaction ease-in-out duration-300" />
                                                  Delete
                                                </a>
                                              )}
                                            </Menu.Item>
                                          </div>
                                        </AllowedTo>
                                      </Menu.Items>
                                    </Transition>
                                  </Menu>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  {cannedTests.length !== 0 ? (
                    <>
                      <Pagination
                        pages={totalPages}
                        setPageNumber={handleChangePageNumber}
                        ref={paginationRef}
                      />
                      <span>
                        <span className="text-sm pr-4 ml-auto text-left-700">
                          <PagingDetailsLabel
                            pageNumber={filterInput.pageNumber}
                            pageSize={filterInput.pageSize}
                            totalRecords={totalRecords}
                          ></PagingDetailsLabel>
                        </span>

                        <SelectPageSize
                          setPageSize={handleChangePageSize}
                          type={"second"}
                          ref={pageSizeRef}
                        />
                      </span>
                    </>
                  ) : (
                    <p className="p-2">No Records Found</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AllowedTo>
  );
};

export default CannedTests;
